import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat, windUniform } from './materials.js';
import { BUILD_MAT_COST, type BuildPieceKind } from './matchRules.js';

export type { BuildPieceKind };
export { BUILD_MAT_COST };
export type TrapVariant = 'spike' | 'stink';
export type BuildMatKind = 'wood' | 'brick' | 'metal';
export type BuildBlockedReason = 'collision' | 'no_materials' | 'invalid_mode' | 'invalid_preview' | null;

const MATERIAL_VISUALS: Record<BuildMatKind, { color: number; emissive: number; emissiveIntensity: number }> = {
  wood:  { color: 0x8B5E3C, emissive: 0x553018, emissiveIntensity: 0.26 },
  brick: { color: 0x6E6274, emissive: 0x2a1a28, emissiveIntensity: 0.22 },
  metal: { color: 0x3D4A5C, emissive: 0x091420, emissiveIntensity: 0.30 },
} as const;

const PIECE_ORDER: BuildPieceKind[] = ['wall', 'floor', 'ramp', 'cone', 'pyramid', 'trap', 'launchpad'];

export const BUILD_MAX_HP: Record<BuildPieceKind, number> = {
  wall: 150,
  floor: 120,
  ramp: 100,
  cone: 100,
  pyramid: 100,
  trap: 100,
  launchpad: 100,
};

const PIECE_LABEL: Record<BuildPieceKind, string> = {
  wall: 'Wall',
  floor: 'Floor',
  ramp: 'Ramp',
  cone: 'Cone',
  pyramid: 'Pyramid',
  trap: 'Trap',
  launchpad: 'Launch Pad',
};

const TRAP_DAMAGE = 55;
const TRAP_RADIUS = 1.35;
const TRAP_COOLDOWN = 1.25;
const TRAP_CONTACT_Y = 1.1;

const LAUNCH_PAD_RADIUS = 1.35;
const LAUNCH_PAD_BOOST_Y = 11;
const LAUNCH_PAD_BOOST_FORWARD = 32;
const LAUNCH_PAD_COOLDOWN = 0.35;
const LAUNCH_PAD_CONTACT_Y = 1.1;

const RAY_MAX = 3;
const EDIT_RAY_MAX = 4.2;
const REINFORCE_MAT_COST = 15;
const REINFORCE_HP_BOOST = 50;
const REINFORCE_MAX_HP_CAP = 200;
const REINFORCE_FLASH_MS = 420;
const PLACE_FLASH_MS = 380;
const ROTATION_SNAP_FLASH_MS = 240;
const TURBO_PLACE_FLASH_MS = 300;
const TURBO_CHAIN_MS = 420;
const REPAIR_MAT_COST = 10;
const REPAIR_HP_GAIN = 50;
const REPAIR_FLASH_MS = 360;
const BUILD_COLLISION_EPS = 0.18;
const LOW_HP_CRACK_RATIO = 0.34;
const HP_BAR_VISIBLE_RANGE = 12;
const HP_BAR_FADE_SEC = 3;

export type PlacedBuild = { mesh: THREE.Mesh; body: CANNON.Body; kind: BuildPieceKind; hp: number; maxHp: number };

export type TrapCheckEntity = { key: string; x: number; y: number; z: number };

export type TrapHitResult = { key: string; damage: number; pos: THREE.Vector3 };

export type LaunchPadBoostResult = {
  key: string;
  boostY: number;
  forwardX: number;
  forwardZ: number;
};

interface PieceSpec {
  geo: THREE.BufferGeometry;
  half: CANNON.Vec3;
  /** Local offset from preview origin along placement normal before rotation. */
  normalOffset: number;
}

function basisFromNormal(normal: THREE.Vector3): THREE.Matrix4 {
  const z = normal.clone().normalize();
  let up = new THREE.Vector3(0, 1, 0);
  if (Math.abs(z.dot(up)) > 0.92) up = new THREE.Vector3(1, 0, 0);
  const x = new THREE.Vector3().crossVectors(up, z).normalize();
  const y = new THREE.Vector3().crossVectors(z, x).normalize();
  const m = new THREE.Matrix4();
  m.makeBasis(x, y, z);
  return m;
}

function pieceSpec(kind: BuildPieceKind): PieceSpec {
  switch (kind) {
    case 'wall':
      return {
        geo: new THREE.BoxGeometry(3, 3, 0.15),
        half: new CANNON.Vec3(1.5, 1.5, 0.075),
        normalOffset: 0.075,
      };
    case 'floor':
      return {
        geo: new THREE.BoxGeometry(3, 0.15, 3),
        half: new CANNON.Vec3(1.5, 0.075, 1.5),
        normalOffset: 0.075,
      };
    case 'ramp':
      return {
        geo: new THREE.BoxGeometry(3, 0.15, 3),
        half: new CANNON.Vec3(1.5, 0.075, 1.5),
        normalOffset: 0.075,
      };
    case 'cone':
      return {
        geo: new THREE.ConeGeometry(1.35, 2.6, 4),
        half: new CANNON.Vec3(1.35, 1.3, 1.35),
        normalOffset: 1.3,
      };
    case 'pyramid': {
      const pyramidGeo = new THREE.ConeGeometry(1.5, 2.0, 4);
      pyramidGeo.applyMatrix4(new THREE.Matrix4().makeRotationY(Math.PI / 4));
      return {
        geo: pyramidGeo,
        half: new CANNON.Vec3(1.5, 1.0, 1.5),
        normalOffset: 1.0,
      };
    }
    case 'trap':
      return {
        geo: new THREE.BoxGeometry(2.8, 0.12, 2.8),
        half: new CANNON.Vec3(1.4, 0.06, 1.4),
        normalOffset: 0.06,
      };
    case 'launchpad':
      return {
        geo: new THREE.BoxGeometry(2.8, 0.1, 2.8),
        half: new CANNON.Vec3(1.4, 0.05, 1.4),
        normalOffset: 0.05,
      };
  }
}

export class FpsBuildPlacer {
  private enabled = false;
  private readonly ghost: THREE.Mesh;
  private ghostGeo: THREE.BufferGeometry;
  private readonly raycaster = new THREE.Raycaster();
  private readonly toonGrad: THREE.DataTexture;
  private readonly ghostMat: THREE.MeshToonMaterial;
  private readonly invalidPreviewStrips: THREE.Mesh[] = [];
  private readonly trapPreviewGroup = new THREE.Group();
  private trapVariant: TrapVariant = 'spike';
  previewPos = new THREE.Vector3();
  previewRot = new THREE.Quaternion();
  private previewTargetBuild: PlacedBuild | null = null;
  private previewYawOffset = 0;
  private hasValidHit = false;
  private readonly placed: PlacedBuild[] = [];
  private readonly trapCooldowns = new Map<string, number>();
  private readonly launchPadCooldowns = new Map<string, number>();
  private piece: BuildPieceKind = 'wall';
  private currentBuildMat: BuildMatKind = 'wood';
  private editHighlightTarget: PlacedBuild | null = null;
  private targetHighlightBuild: PlacedBuild | null = null;
  private readonly targetHighlightMat: THREE.MeshBasicMaterial;
  private targetHighlightMesh: THREE.Mesh | null = null;
  private buildHpBars = new Map<THREE.Mesh, { sprite: THREE.Sprite; timer: number }>();
  private buildMaterialFlashTimer = 0;
  private rotationSnapFlashUntil = 0;
  private lastPlaceAt = 0;
  private lastBlockedReason: BuildBlockedReason = null;
  private readonly scene: THREE.Scene;

  constructor(
    _scene: THREE.Scene,
    private readonly world: CANNON.World,
    private readonly floorMat: CANNON.Material,
    private readonly sceneRoot: THREE.Group,
    private readonly camera: THREE.Camera,
  ) {
    this.scene = _scene;
    this.toonGrad = createToonGradientMap();
    this.ghostMat = toonMat(0x66eedd, this.toonGrad, { emissive: 0x22cc88, emissiveIntensity: 0.4 });
    this.ghostMat.transparent = true;
    this.ghostMat.opacity = 0.5;
    this.ghostMat.depthWrite = false;
    this.ghostMat.onBeforeCompile = (shader) => {
      shader.uniforms.windTime = windUniform;
      shader.vertexShader = 'uniform float windTime;\nvarying vec3 vWorldPos;\n' + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
        #include <begin_vertex>
        vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        `
      );
      shader.fragmentShader = 'uniform float windTime;\nvarying vec3 vWorldPos;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        `
        #include <dithering_fragment>
        float scanline = sin(vWorldPos.y * 12.0 - windTime * 4.5) * 0.5 + 0.5;
        float gridX = step(0.92, fract(vWorldPos.x * 2.0));
        float gridY = step(0.92, fract(vWorldPos.y * 2.0));
        float gridZ = step(0.92, fract(vWorldPos.z * 2.0));
        float gridLine = max(gridX, max(gridY, gridZ));
        vec3 holoColor = vec3(0.1, 0.6, 1.0);
        gl_FragColor.rgb = mix(gl_FragColor.rgb, holoColor, 0.4 + 0.6 * gridLine);
        gl_FragColor.rgb += holoColor * (scanline * 0.28);
        gl_FragColor.a = mix(gl_FragColor.a, 0.85, gridLine * 0.5 + scanline * 0.15);
        `
      );
    };
    this.ghostGeo = pieceSpec(this.piece).geo;
    this.ghost = new THREE.Mesh(this.ghostGeo, this.ghostMat);
    this.ghost.raycast = () => {};
    this.ghost.visible = false;
    this.buildTrapPreviewMeshes();
    this.ghost.add(this.trapPreviewGroup);
    this.sceneRoot.add(this.ghost);

    const stripMat = new THREE.MeshBasicMaterial({
      color: 0xff1f2d,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      depthTest: false,
    });
    for (const angle of [Math.PI / 4, -Math.PI / 4]) {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(2.9, 0.16), stripMat);
      strip.rotation.z = angle;
      strip.position.z = 0.12;
      strip.renderOrder = 20;
      strip.raycast = () => {};
      strip.visible = false;
      this.ghost.add(strip);
      this.invalidPreviewStrips.push(strip);
    }

    this.targetHighlightMat = new THREE.MeshBasicMaterial({
      color: 0x33f6ff,
      transparent: true,
      opacity: 0.58,
      wireframe: true,
      depthWrite: false,
      depthTest: false,
    });
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.ghost.visible = false;
      this.previewTargetBuild = null;
      this.updateTargetHighlight(false);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getPieceKind(): BuildPieceKind {
    return this.piece;
  }

  getMatCost(kind: BuildPieceKind = this.piece): number {
    return BUILD_MAT_COST[kind];
  }

  getPieceLabel(kind: BuildPieceKind = this.piece): string {
    return PIECE_LABEL[kind];
  }

  getBuildMat(): BuildMatKind {
    return this.currentBuildMat;
  }

  cycleBuildMat(): void {
    const order: BuildMatKind[] = ['wood', 'brick', 'metal'];
    const idx = order.indexOf(this.currentBuildMat);
    this.currentBuildMat = order[(idx + 1) % order.length]!;
    // Flash ghost preview
    this.ghostMat.emissiveIntensity = 1.5;
    this.buildMaterialFlashTimer = 0.18;
  }

  /** Cost hint for HUD: current piece + all standard costs. */
  getCostHintText(): string {
    const c = BUILD_MAT_COST[this.piece];
    return `${PIECE_LABEL[this.piece]} · ${c} mats  (wall/floor/ramp/cone/pyramid = 10, trap = 15, launch pad = 20)`;
  }

  rotatePreview(): void {
    if (this.piece === 'trap') {
      this.trapVariant = this.trapVariant === 'spike' ? 'stink' : 'spike';
      this.syncTrapPreviewVisibility();
      return;
    }
    this.previewYawOffset = (this.previewYawOffset + Math.PI / 2) % (Math.PI * 2);
    this.rotationSnapFlashUntil = performance.now() + ROTATION_SNAP_FLASH_MS;
  }

  getTrapVariant(): TrapVariant {
    return this.trapVariant;
  }

  hasValidPreview(): boolean {
    return this.enabled && this.hasValidHit;
  }

  getBlockedReason(): BuildBlockedReason {
    return this.lastBlockedReason;
  }

  getBlockedReasonText(): string | null {
    switch (this.lastBlockedReason) {
      case 'collision':
        return 'Blocked';
      case 'no_materials':
        return `Need ${this.getMatCost()} mats`;
      case 'invalid_mode':
        return 'Builds disabled';
      case 'invalid_preview':
        return 'Aim at surface';
      default:
        return null;
    }
  }

  updateBlockedReason(materialsAvailable: number, buildEnabled: boolean, prepaid = false): BuildBlockedReason {
    if (!buildEnabled) this.lastBlockedReason = 'invalid_mode';
    else if (!this.enabled || !this.hasValidHit) this.lastBlockedReason = 'invalid_preview';
    else if (!prepaid && materialsAvailable < this.getMatCost()) this.lastBlockedReason = 'no_materials';
    else if (this.hasPreviewCollision()) this.lastBlockedReason = 'collision';
    else this.lastBlockedReason = null;
    return this.lastBlockedReason;
  }

  /** Pulse ghost emissive/opacity — valid = cyan-green, invalid = red. */
  updatePreviewVisual(now: number, canPlace: boolean): void {
    if (!this.enabled || !this.ghost.visible) return;
    const pulse = 0.5 + 0.5 * Math.sin(now * 0.008);
    const showBlocked = !canPlace;
    const snapFlash = this.rotationSnapFlashUntil > now;
    if (snapFlash) {
      const t = 1 - (this.rotationSnapFlashUntil - now) / ROTATION_SNAP_FLASH_MS;
      const flash = Math.sin(t * Math.PI);
      this.ghostMat.emissive.setHex(canPlace ? 0x88ffee : 0xff8888);
      this.ghostMat.emissiveIntensity = 0.55 + flash * 1.35;
      this.ghostMat.opacity = canPlace ? 0.58 + flash * 0.22 : 0.5 + flash * 0.18;
      for (const strip of this.invalidPreviewStrips) {
        strip.visible = showBlocked;
      }
      if (this.piece === 'trap') this.syncTrapPreviewVisibility();
      return;
    }
    if (this.piece === 'trap') {
      this.syncTrapPreviewVisibility();
      if (this.trapVariant === 'spike') {
        this.ghostMat.color.setHex(canPlace ? 0xcc4455 : 0xff6666);
        this.ghostMat.emissive.setHex(canPlace ? 0x661122 : 0xcc2222);
      } else {
        this.ghostMat.color.setHex(canPlace ? 0x55aa55 : 0xff6666);
        this.ghostMat.emissive.setHex(canPlace ? 0x224422 : 0xcc2222);
      }
      this.ghostMat.emissiveIntensity = 0.42 + pulse * 0.5;
      this.ghostMat.opacity = canPlace ? 0.38 + pulse * 0.12 : 0.34 + pulse * 0.1;
      for (const strip of this.invalidPreviewStrips) {
        strip.visible = showBlocked;
        strip.scale.setScalar(1 + pulse * 0.08);
      }
      return;
    }
    // Phase 30X-4: stronger affordance — cyan when free, hot red when blocked/no mats
    if (canPlace) {
      this.ghostMat.color.setHex(0x5ef0e0);
      this.ghostMat.emissive.setHex(0x28e8b0);
      this.ghostMat.emissiveIntensity = 0.48 + pulse * 0.72;
      this.ghostMat.opacity = 0.55 + pulse * 0.16;
    } else {
      const noMats = this.lastBlockedReason === 'no_materials';
      this.ghostMat.color.setHex(noMats ? 0xff3355 : 0xff6666);
      this.ghostMat.emissive.setHex(noMats ? 0xff1040 : 0xcc2222);
      this.ghostMat.emissiveIntensity = (noMats ? 0.72 : 0.48) + pulse * 0.7;
      this.ghostMat.opacity = (noMats ? 0.42 : 0.34) + pulse * 0.14;
    }
    for (const strip of this.invalidPreviewStrips) {
      strip.visible = showBlocked;
      strip.scale.setScalar(1 + pulse * 0.08);
    }
  }

  private hasPreviewCollision(): boolean {
    if (!this.enabled || !this.hasValidHit) return false;
    const spec = pieceSpec(this.piece);
    const minDist = Math.max(spec.half.x, spec.half.y, spec.half.z) * 0.72;
    for (const build of this.placed) {
      if (build === this.previewTargetBuild) continue;
      if (build.mesh.position.distanceToSquared(this.previewPos) < minDist * minDist) return true;
    }
    return false;
  }

  cyclePiece(delta = 1): void {
    const idx = PIECE_ORDER.indexOf(this.piece);
    const next = PIECE_ORDER[(idx + delta + PIECE_ORDER.length) % PIECE_ORDER.length]!;
    this.setPiece(next);
  }

  setPiece(kind: BuildPieceKind): void {
    if (this.piece === kind) return;
    this.piece = kind;
    this.previewYawOffset = 0;
    if (kind === 'trap') this.trapVariant = 'spike';
    this.ghostGeo.dispose();
    this.ghostGeo = pieceSpec(kind).geo;
    this.ghost.geometry = this.ghostGeo;
    this.syncTrapPreviewVisibility();
  }

  private buildTrapPreviewMeshes(): void {
    const spikeMat = new THREE.MeshBasicMaterial({
      color: 0xff4455,
      transparent: true,
      opacity: 0.62,
      depthWrite: false,
    });
    const spikePositions: [number, number][] = [
      [-0.9, -0.7], [0.9, -0.7], [-0.9, 0.7], [0.9, 0.7], [0, 0],
    ];
    for (const [x, z] of spikePositions) {
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.42, 5), spikeMat);
      spike.position.set(x, 0.28, z);
      spike.name = 'trap-spike';
      spike.raycast = () => {};
      this.trapPreviewGroup.add(spike);
    }

    const stinkMat = new THREE.MeshBasicMaterial({
      color: 0x66ff66,
      transparent: true,
      opacity: 0.34,
      depthWrite: false,
    });
    const stinkCloud = new THREE.Mesh(new THREE.SphereGeometry(0.95, 10, 8), stinkMat);
    stinkCloud.position.y = 0.55;
    stinkCloud.name = 'trap-stink';
    stinkCloud.raycast = () => {};
    this.trapPreviewGroup.add(stinkCloud);
    this.syncTrapPreviewVisibility();
  }

  private syncTrapPreviewVisibility(): void {
    const showTrap = this.piece === 'trap';
    this.trapPreviewGroup.visible = showTrap;
    for (const child of this.trapPreviewGroup.children) {
      if (child.name === 'trap-spike') child.visible = showTrap && this.trapVariant === 'spike';
      if (child.name === 'trap-stink') child.visible = showTrap && this.trapVariant === 'stink';
    }
  }

  private woodBuildMat(): THREE.MeshToonMaterial {
    return toonMat(0xc4883e, this.toonGrad, { emissive: 0x553018, emissiveIntensity: 0.26 });
  }

  private metalReinforcedMat(): THREE.MeshToonMaterial {
    return toonMat(0x6b8499, this.toonGrad, { emissive: 0x2a4058, emissiveIntensity: 0.38 });
  }

  private applyPreviewYawOffset(): void {
    if (Math.abs(this.previewYawOffset) < 1e-6) return;
    const offset = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), this.previewYawOffset);
    this.previewRot.multiply(offset);
  }

  private cameraYaw(): number {
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    dir.y = 0;
    if (dir.lengthSq() < 1e-6) return 0;
    dir.normalize();
    return Math.atan2(-dir.x, -dir.z);
  }

  updateFromCamera(): void {
    if (!this.enabled) {
      this.hasValidHit = false;
      this.previewTargetBuild = null;
      return;
    }

    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
    this.raycaster.far = RAY_MAX;
    const hits = this.raycaster.intersectObject(this.sceneRoot, true);
    const hit = hits[0];
    if (!hit || !hit.face) {
      this.hasValidHit = false;
      this.previewTargetBuild = null;
      this.ghost.visible = false;
      return;
    }

    this.previewTargetBuild = this.findBuildFromObject(hit.object);

    const n = hit.face.normal.clone();
    n.transformDirection(hit.object.matrixWorld);
    n.normalize();

    const spec = pieceSpec(this.piece);
    const offset = n.clone().multiplyScalar(spec.normalOffset);

    if (this.piece === 'floor' && Math.abs(n.y) > 0.55) {
      this.previewRot.setFromEuler(new THREE.Euler(0, this.cameraYaw(), 0));
      this.previewPos.copy(hit.point).add(new THREE.Vector3(0, spec.normalOffset, 0));
    } else if (
      (this.piece === 'trap' || this.piece === 'launchpad') &&
      Math.abs(n.y) > 0.55
    ) {
      this.previewRot.setFromEuler(new THREE.Euler(0, this.cameraYaw(), 0));
      this.previewPos.copy(hit.point).add(new THREE.Vector3(0, spec.normalOffset, 0));
    } else if (this.piece === 'cone' && Math.abs(n.y) > 0.55) {
      this.previewRot.setFromEuler(new THREE.Euler(0, this.cameraYaw(), 0));
      this.previewPos.copy(hit.point).add(new THREE.Vector3(0, spec.normalOffset, 0));
    } else if (this.piece === 'pyramid' && Math.abs(n.y) > 0.55) {
      this.previewRot.setFromEuler(new THREE.Euler(0, this.cameraYaw(), 0));
      this.previewPos.copy(hit.point).add(new THREE.Vector3(0, spec.normalOffset, 0));
    } else if (this.piece === 'ramp') {
      const m = basisFromNormal(n);
      this.previewRot.setFromRotationMatrix(m);
      const rampTilt = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 4);
      this.previewRot.multiply(rampTilt);
      this.previewPos.copy(hit.point).add(offset);
    } else {
      const m = basisFromNormal(n);
      this.previewRot.setFromRotationMatrix(m);
      this.previewPos.copy(hit.point).add(offset);
    }

    this.applyPreviewYawOffset();

    this.ghost.position.copy(this.previewPos);
    this.ghost.quaternion.copy(this.previewRot);
    this.ghost.visible = true;
    this.hasValidHit = true;
  }

  tryPlace(opts?: { turbo?: boolean }): boolean {
    if (!this.enabled || !this.hasValidHit) {
      this.lastBlockedReason = 'invalid_preview';
      return false;
    }
    if (this.hasPreviewCollision()) {
      this.lastBlockedReason = 'collision';
      return false;
    }

    const spec = pieceSpec(this.piece);
    const visual = MATERIAL_VISUALS[this.currentBuildMat];
    const pieceMat =
      this.piece === 'trap'
        ? toonMat(0xcc3344, this.toonGrad, { emissive: 0x661122, emissiveIntensity: 0.55 })
        : this.piece === 'launchpad'
          ? toonMat(0x2ee6ff, this.toonGrad, { emissive: 0x0a4466, emissiveIntensity: 0.48 })
          : toonMat(visual.color, this.toonGrad, { emissive: visual.emissive, emissiveIntensity: visual.emissiveIntensity });
    const mesh = new THREE.Mesh(spec.geo.clone(), pieceMat);
    mesh.position.copy(this.previewPos);
    mesh.quaternion.copy(this.previewRot);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.isPlayerBuild = true;
    mesh.userData.buildOwner = 'player';
    mesh.userData.buildKind = this.piece;
    mesh.userData.buildMatKind = this.currentBuildMat;
    if (this.piece === 'trap') mesh.userData.trapVariant = this.trapVariant;
    const maxHp = BUILD_MAX_HP[this.piece];
    mesh.userData.buildHp = maxHp;
    mesh.userData.buildMaxHp = maxHp;
    mesh.userData.baseEmissive = pieceMat.emissive.getHex();
    mesh.userData.baseEmissiveIntensity = pieceMat.emissiveIntensity;
    if (this.piece === 'launchpad') {
      const arrowMat = toonMat(0xffffff, this.toonGrad, { emissive: 0x88eeff, emissiveIntensity: 0.35 });
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.72, 4), arrowMat);
      arrow.rotation.x = -Math.PI / 2;
      arrow.position.set(0, 0.14, -0.62);
      arrow.castShadow = true;
      mesh.add(arrow);
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(mesh.quaternion);
      forward.y = 0;
      if (forward.lengthSq() > 1e-6) forward.normalize();
      else forward.set(0, 0, -1);
      mesh.userData.launchForward = { x: forward.x, z: forward.z };
    }
    this.attachPlayerOwnerOutline(mesh, spec.geo);
    // Metal: store glow timer for placement flash
    if (this.currentBuildMat === 'metal' && this.piece !== 'trap' && this.piece !== 'launchpad') {
      mesh.userData.glowTimer = 1.5;
    }
    this.sceneRoot.add(mesh);

    const body = new CANNON.Body({ mass: 0, material: this.floorMat });
    body.addShape(new CANNON.Box(spec.half));
    body.position.set(mesh.position.x, mesh.position.y, mesh.position.z);
    body.quaternion.set(mesh.quaternion.x, mesh.quaternion.y, mesh.quaternion.z, mesh.quaternion.w);
    this.world.addBody(body);

    const now = performance.now();
    const rapidChain = now - this.lastPlaceAt < TURBO_CHAIN_MS;
    const turbo = opts?.turbo === true || rapidChain;
    mesh.userData.placeFlashUntil = now + PLACE_FLASH_MS;
    if (turbo) {
      mesh.userData.turboFlashUntil = now + TURBO_PLACE_FLASH_MS;
    }
    this.lastPlaceAt = now;
    this.refreshBuildVisual({ mesh, body, kind: this.piece, hp: maxHp, maxHp }, false, now);

    this.placed.push({ mesh, body, kind: this.piece, hp: maxHp, maxHp });
    this.lastBlockedReason = null;
    return true;
  }

  private attachPlayerOwnerOutline(mesh: THREE.Mesh, geo: THREE.BufferGeometry): void {
    const ownerMat = new THREE.MeshBasicMaterial({
      color: 0x62f6ff,
      transparent: true,
      opacity: 0.11,
      wireframe: true,
      depthWrite: false,
    });
    const ownerOutline = new THREE.Mesh(geo.clone(), ownerMat);
    ownerOutline.name = 'player-build-owner-outline';
    ownerOutline.raycast = () => {};
    ownerOutline.renderOrder = 7;
    ownerOutline.scale.setScalar(1.012);
    mesh.add(ownerOutline);
    mesh.userData.ownerOutline = ownerOutline;
  }

  private findBuildIndexFromObject(object: THREE.Object3D): number {
    let node: THREE.Object3D | null = object;
    while (node) {
      if (node instanceof THREE.Mesh && node.userData.isPlayerBuild === true) {
        return this.placed.findIndex((p) => p.mesh === node);
      }
      node = node.parent;
    }
    return -1;
  }

  private findBuildFromObject(object: THREE.Object3D): PlacedBuild | null {
    const idx = this.findBuildIndexFromObject(object);
    return idx >= 0 ? this.placed[idx] ?? null : null;
  }

  /** Apply weapon damage to a build hit by raycast object. */
  damageBuildFromHit(object: THREE.Object3D, amount: number): { hit: boolean; destroyed: boolean } {
    const idx = this.findBuildIndexFromObject(object);
    if (idx === -1) return { hit: false, destroyed: false };
    const destroyed = this.damageBuildAtIndex(idx, amount);
    return { hit: true, destroyed };
  }

  /** Apply damage to a placed build by index. Returns true if the build was destroyed. */
  damageBuildAtIndex(idx: number, amount: number): boolean {
    const build = this.placed[idx];
    if (!build || amount <= 0) return false;
    build.hp = Math.max(0, build.hp - amount);
    build.mesh.userData.buildHp = build.hp;
    this.refreshBuildVisual(build, build === this.editHighlightTarget, performance.now());
    // Show HP bar above damaged build
    this.updateBuildHpBar(build.mesh, build.hp / build.maxHp);
    if (build.hp <= 0) {
      this.cleanupHpBar(build.mesh);
      this.removeBuildAtIndex(idx, false);
      return true;
    }
    return false;
  }

  private updateBuildHpBar(mesh: THREE.Mesh, hpRatio: number): void {
    let entry = this.buildHpBars.get(mesh);
    if (!entry) {
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 8;
      const tex = new THREE.CanvasTexture(canvas);
      const spriteMat = new THREE.SpriteMaterial({ map: tex, depthTest: false });
      const sprite = new THREE.Sprite(spriteMat);
      sprite.scale.set(1.2, 0.15, 1);
      sprite.position.copy(mesh.position);
      sprite.position.y += 1.5;
      this.scene.add(sprite);
      entry = { sprite, timer: HP_BAR_FADE_SEC };
      this.buildHpBars.set(mesh, entry);
    }
    entry.timer = HP_BAR_FADE_SEC;
    entry.sprite.position.copy(mesh.position);
    entry.sprite.position.y += 1.5;
    // Redraw canvas
    const spriteMat = entry.sprite.material as THREE.SpriteMaterial;
    const canvas = (spriteMat.map as THREE.CanvasTexture).image as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, 64, 8);
    ctx.fillStyle = '#333333';
    ctx.fillRect(0, 0, 64, 8);
    const fillW = Math.round(62 * Math.max(0, hpRatio));
    ctx.fillStyle = hpRatio > 0.66 ? '#44cc44' : hpRatio > 0.33 ? '#cccc44' : '#cc4444';
    ctx.fillRect(1, 1, fillW, 6);
    (spriteMat.map as THREE.CanvasTexture).needsUpdate = true;
  }

  private cleanupHpBar(mesh: THREE.Mesh): void {
    const entry = this.buildHpBars.get(mesh);
    if (!entry) return;
    this.scene.remove(entry.sprite);
    (entry.sprite.material as THREE.SpriteMaterial).map?.dispose();
    entry.sprite.material.dispose();
    this.buildHpBars.delete(mesh);
  }

  private updateCrackOverlay(build: PlacedBuild): void {
    const existing = build.mesh.userData.crackOverlay as THREE.Mesh | undefined;
    if (build.hp / build.maxHp >= LOW_HP_CRACK_RATIO || build.hp <= 0) {
      if (existing) {
        existing.removeFromParent();
        existing.geometry.dispose();
        if (existing.material instanceof THREE.Material) existing.material.dispose();
        delete build.mesh.userData.crackOverlay;
      }
      return;
    }

    let crack = existing;
    if (!crack) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xff3434,
        transparent: true,
        opacity: 0.34,
        wireframe: true,
        depthWrite: false,
        depthTest: false,
      });
      crack = new THREE.Mesh(build.mesh.geometry.clone(), mat);
      crack.name = 'low-hp-crack-overlay';
      crack.raycast = () => {};
      crack.renderOrder = 21;
      build.mesh.add(crack);
      build.mesh.userData.crackOverlay = crack;
    }
    const mat = crack.material as THREE.MeshBasicMaterial;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.045);
    mat.opacity = 0.26 + pulse * 0.24;
    crack.scale.setScalar(1.018 + pulse * 0.018);
  }

  /** Call from the main update loop with delta time (seconds). */
  updateHpBars(dt: number, cameraPos: THREE.Vector3): void {
    for (const [mesh, entry] of this.buildHpBars) {
      entry.timer -= dt;
      entry.sprite.position.copy(mesh.position);
      entry.sprite.position.y += 1.5;
      const camDist = entry.sprite.position.distanceTo(cameraPos);
      const build = this.placed.find((p) => p.mesh === mesh);
      const hpRatio = build ? build.hp / build.maxHp : 1;
      if (hpRatio >= 0.999) {
        this.cleanupHpBar(mesh);
        continue;
      }
      entry.sprite.visible = entry.timer > 0 && camDist < HP_BAR_VISIBLE_RANGE;
      if (entry.timer <= -1) {
        this.cleanupHpBar(mesh);
      }
    }
    // Animate metal glow & material flash timer
    if (this.buildMaterialFlashTimer > 0) {
      this.buildMaterialFlashTimer -= dt;
      this.ghostMat.emissiveIntensity = Math.max(0, this.buildMaterialFlashTimer / 0.18) * 1.5;
    }
    // Animate metal placement glow on placed builds
    for (const build of this.placed) {
      this.updateCrackOverlay(build);
      const gt = build.mesh.userData.glowTimer as number | undefined;
      if (gt != null && gt > 0) {
        build.mesh.userData.glowTimer = Math.max(0, gt - dt);
        const t = (build.mesh.userData.glowTimer as number) / 1.5;
        const mat = build.mesh.material as THREE.MeshToonMaterial;
        if (t > 0) {
          mat.emissive.setRGB(0.1 * t, 0.8 * t, 1.0 * t);
          mat.emissiveIntensity = t * 1.2;
        } else {
          // Settle to metal base emissive
          mat.emissive.setHex(MATERIAL_VISUALS.metal.emissive);
          mat.emissiveIntensity = MATERIAL_VISUALS.metal.emissiveIntensity;
          delete build.mesh.userData.glowTimer;
        }
      }
    }
  }

  private removeBuildAtIndex(idx: number, refundMats: boolean): number {
    const build = this.placed[idx];
    if (!build) return 0;
    const { mesh, body } = build;
    this.cleanupHpBar(mesh);
    this.world.removeBody(body);
    mesh.removeFromParent();
    mesh.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || obj === mesh) return;
      obj.geometry.dispose();
      if (Array.isArray(obj.material)) obj.material.forEach((mat) => mat.dispose());
      else obj.material.dispose();
    });
    (mesh.geometry as THREE.BufferGeometry).dispose();
    if (Array.isArray(mesh.material)) mesh.material.forEach((mat) => mat.dispose());
    else mesh.material.dispose();
    this.placed.splice(idx, 1);
    return refundMats ? Math.floor(8 + Math.random() * 8) : 0;
  }

  private findBuildFromRay(camera: THREE.Camera, maxDist: number): PlacedBuild | null {
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = maxDist;
    const hits = this.raycaster.intersectObject(this.sceneRoot, true);
    const hit = hits[0];
    if (!hit) return null;

    let node: THREE.Object3D | null = hit.object;
    while (node) {
      if (node instanceof THREE.Mesh && node.userData.isPlayerBuild === true) {
        const build = this.placed.find((p) => p.mesh === node);
        return build ?? null;
      }
      node = node.parent;
    }
    return null;
  }

  getTargetedBuild(maxDist = EDIT_RAY_MAX): PlacedBuild | null {
    const rayTarget = this.findBuildFromRay(this.camera, maxDist);
    return rayTarget ?? this.previewTargetBuild;
  }

  updateTargetHighlight(active: boolean, now = performance.now()): void {
    const target = active ? this.getTargetedBuild() : null;
    if (!target) {
      if (this.targetHighlightMesh) this.targetHighlightMesh.visible = false;
      this.targetHighlightBuild = null;
      return;
    }

    if (!this.targetHighlightMesh || this.targetHighlightBuild !== target) {
      if (this.targetHighlightMesh) {
        this.targetHighlightMesh.removeFromParent();
        this.targetHighlightMesh.geometry.dispose();
      }
      this.targetHighlightMesh = new THREE.Mesh(target.mesh.geometry.clone(), this.targetHighlightMat);
      this.targetHighlightMesh.renderOrder = 18;
      this.targetHighlightMesh.raycast = () => {};
      this.sceneRoot.add(this.targetHighlightMesh);
      this.targetHighlightBuild = target;
    }

    const pulse = 0.65 + 0.35 * Math.sin(now * 0.01);
    this.targetHighlightMat.opacity = 0.38 + pulse * 0.24;
    this.targetHighlightMesh.visible = true;
    this.targetHighlightMesh.position.copy(target.mesh.position);
    this.targetHighlightMesh.quaternion.copy(target.mesh.quaternion);
    this.targetHighlightMesh.scale.copy(target.mesh.scale).multiplyScalar(1.018 + pulse * 0.01);
  }

  private refreshBuildVisual(build: PlacedBuild, isHighlighted: boolean, now: number): void {
    const mat = build.mesh.material as THREE.MeshToonMaterial;
    const baseE = (build.mesh.userData.baseEmissive as number | undefined) ?? 0x442208;
    const baseI = (build.mesh.userData.baseEmissiveIntensity as number | undefined) ?? 0.2;
    const damageT = 1 - build.hp / build.maxHp;
    const hpRatio = build.hp / build.maxHp;

    const emissive = new THREE.Color(baseE);
    if (damageT > 0.001) {
      emissive.lerp(new THREE.Color(0xff7722), damageT * 0.78);
    }
    let intensity = baseI + damageT * 0.42;

    if (hpRatio < LOW_HP_CRACK_RATIO) {
      const flicker = 0.5 + 0.5 * Math.sin(now * 0.035);
      emissive.lerp(new THREE.Color(0xff3a22), 0.22 + flicker * 0.24);
      intensity += 0.22 + flicker * 0.38;
    }

    if (isHighlighted) {
      const pulse = 0.55 + 0.45 * Math.sin(now * 0.007);
      emissive.lerp(new THREE.Color(0x4488ff), pulse * 0.62);
      intensity += pulse * 0.38;
    }

    const reinforceUntil = build.mesh.userData.reinforceFlashUntil as number | undefined;
    if (reinforceUntil != null && now < reinforceUntil) {
      const t = 1 - (reinforceUntil - now) / REINFORCE_FLASH_MS;
      const flash = Math.sin(t * Math.PI);
      emissive.lerp(new THREE.Color(0x44ff66), flash * 0.92);
      intensity += flash * 0.85;
    }

    const repairUntil = build.mesh.userData.repairFlashUntil as number | undefined;
    if (repairUntil != null && now < repairUntil) {
      const t = 1 - (repairUntil - now) / REPAIR_FLASH_MS;
      const flash = Math.sin(t * Math.PI);
      emissive.lerp(new THREE.Color(0x55ffdd), flash * 0.86);
      intensity += flash * 0.48;
      const scale = 1 + flash * 0.08;
      build.mesh.scale.set(scale, scale, scale);
    }

    const turboUntil = build.mesh.userData.turboFlashUntil as number | undefined;
    if (turboUntil != null && now < turboUntil) {
      const t = 1 - (turboUntil - now) / TURBO_PLACE_FLASH_MS;
      const streak = Math.sin(t * Math.PI);
      emissive.lerp(new THREE.Color(0x44f0ff), streak * 0.95);
      intensity += streak * 0.72;
      const scale = 1 + streak * 0.06;
      build.mesh.scale.set(scale, scale, scale);
    }

    const placeUntil = build.mesh.userData.placeFlashUntil as number | undefined;
    if (placeUntil != null && now < placeUntil) {
      const t = 1 - (placeUntil - now) / PLACE_FLASH_MS;
      const flash = Math.sin(t * Math.PI);
      const turboActive = turboUntil != null && now < turboUntil;
      if (!turboActive) {
        emissive.lerp(new THREE.Color(0xffddaa), flash * 0.72);
        intensity += flash * 0.48;
        const scale = 0.8 + 0.32 * Math.sin(t * Math.PI * 1.5) * (1 - t);
        build.mesh.scale.set(scale, scale, scale);
      } else {
        intensity += flash * 0.22;
      }
    } else if ((repairUntil == null || now >= repairUntil) && (turboUntil == null || now >= turboUntil)) {
      build.mesh.scale.set(1, 1, 1);
    }

    mat.emissive.copy(emissive);
    mat.emissiveIntensity = intensity;
    const ownerOutline = build.mesh.userData.ownerOutline as THREE.Mesh | undefined;
    if (ownerOutline?.material instanceof THREE.MeshBasicMaterial) {
      const repairActive = repairUntil != null && now < repairUntil;
      const pulse = repairActive ? 0.5 + 0.5 * Math.sin(now * 0.03) : 0;
      ownerOutline.material.color.setHex(0x62f6ff);
      ownerOutline.material.opacity = repairActive ? 0.2 + pulse * 0.08 : hpRatio < LOW_HP_CRACK_RATIO ? 0.16 : 0.11;
      ownerOutline.scale.setScalar(repairActive ? 1.018 + pulse * 0.01 : 1.012);
    }
    this.updateCrackOverlay(build);
  }

  /** When not in build mode, highlight the build under crosshair for editing. */
  updateEditHighlight(camera: THREE.Camera): void {
    const now = performance.now();
    if (this.enabled) {
      if (this.editHighlightTarget) {
        this.refreshBuildVisual(this.editHighlightTarget, false, now);
        this.editHighlightTarget = null;
      }
      return;
    }

    const target = this.findBuildFromRay(camera, EDIT_RAY_MAX);
    if (this.editHighlightTarget && this.editHighlightTarget !== target) {
      this.refreshBuildVisual(this.editHighlightTarget, false, now);
    }
    this.editHighlightTarget = target;
    if (target) {
      this.refreshBuildVisual(target, true, now);
    }

    for (const build of this.placed) {
      const placeUntil = build.mesh.userData.placeFlashUntil as number | undefined;
      const turboUntil = build.mesh.userData.turboFlashUntil as number | undefined;
      if (
        (placeUntil != null && now < placeUntil) ||
        (turboUntil != null && now < turboUntil)
      ) {
        this.refreshBuildVisual(build, build === this.editHighlightTarget, now);
      }
    }
  }

  getPlacedBuilds(): PlacedBuild[] {
    return this.placed;
  }

  getTargetedBuildHp(): { hp: number; maxHp: number } | null {
    if (!this.editHighlightTarget) return null;
    return { hp: this.editHighlightTarget.hp, maxHp: this.editHighlightTarget.maxHp };
  }

  getTargetedBuildPrompt(materialsAvailable = Number.POSITIVE_INFINITY): string | null {
    const build = this.getTargetedBuild();
    if (!build) return null;
    const pct = Math.max(0, Math.min(100, Math.round((build.hp / build.maxHp) * 100)));
    if (build.hp < build.maxHp) {
      return materialsAvailable >= 1 ? `[R] Repair · ${pct}% HP` : `${pct}% HP · Need mats`;
    }
    return `${pct}% HP`;
  }

  repairTargetedBuild(materialsAvailable: number): { repaired: boolean; cost: number; hpGain: number } {
    const build = this.getTargetedBuild();
    if (!build || materialsAvailable <= 0 || build.hp >= build.maxHp) {
      return { repaired: false, cost: 0, hpGain: 0 };
    }

    const missingHp = build.maxHp - build.hp;
    const affordableHp = Math.floor((materialsAvailable / REPAIR_MAT_COST) * REPAIR_HP_GAIN);
    const hpGain = Math.min(REPAIR_HP_GAIN, missingHp, affordableHp);
    if (hpGain <= 0) return { repaired: false, cost: 0, hpGain: 0 };

    const cost = Math.max(1, Math.ceil((hpGain / REPAIR_HP_GAIN) * REPAIR_MAT_COST));
    if (cost > materialsAvailable) return { repaired: false, cost: 0, hpGain: 0 };

    build.hp = Math.min(build.maxHp, build.hp + hpGain);
    build.mesh.userData.buildHp = build.hp;
    const now = performance.now();
    build.mesh.userData.repairFlashUntil = now + REPAIR_FLASH_MS;
    this.refreshBuildVisual(build, build === this.editHighlightTarget, now);
    this.updateBuildHpBar(build.mesh, build.hp / build.maxHp);
    return { repaired: true, cost, hpGain };
  }

  hasRemovableBuildInLook(camera: THREE.Camera, maxDist = EDIT_RAY_MAX): boolean {
    return this.findBuildFromRay(camera, maxDist) !== null;
  }

  /** Spike traps damage entities standing on them (cooldown per trap + entity). */
  checkTraps(entities: TrapCheckEntity[], now: number): TrapHitResult[] {
    const hits: TrapHitResult[] = [];
    for (let ti = 0; ti < this.placed.length; ti++) {
      const trap = this.placed[ti]!;
      if (trap.kind !== 'trap') continue;
      const tx = trap.mesh.position.x;
      const tz = trap.mesh.position.z;
      const ty = trap.mesh.position.y;

      for (const ent of entities) {
        const dx = ent.x - tx;
        const dz = ent.z - tz;
        if (dx * dx + dz * dz > TRAP_RADIUS * TRAP_RADIUS) continue;
        if (Math.abs(ent.y - ty - 0.06) > TRAP_CONTACT_Y) continue;

        const cdKey = `${ti}:${ent.key}`;
        const readyAt = this.trapCooldowns.get(cdKey) ?? 0;
        if (now < readyAt) continue;

        this.trapCooldowns.set(cdKey, now + TRAP_COOLDOWN);
        hits.push({
          key: ent.key,
          damage: TRAP_DAMAGE,
          pos: new THREE.Vector3(tx, ty + 0.2, tz),
        });
      }
    }
    return hits;
  }

  /** Launch pads boost entities standing on them (cooldown per pad + entity). */
  checkLaunchPads(entities: TrapCheckEntity[], now: number): LaunchPadBoostResult[] {
    const hits: LaunchPadBoostResult[] = [];
    for (let pi = 0; pi < this.placed.length; pi++) {
      const pad = this.placed[pi]!;
      if (pad.kind !== 'launchpad') continue;
      const tx = pad.mesh.position.x;
      const tz = pad.mesh.position.z;
      const ty = pad.mesh.position.y;
      const fwd = pad.mesh.userData.launchForward as { x: number; z: number } | undefined;
      const forwardX = fwd?.x ?? 0;
      const forwardZ = fwd?.z ?? -1;

      for (const ent of entities) {
        const dx = ent.x - tx;
        const dz = ent.z - tz;
        if (dx * dx + dz * dz > LAUNCH_PAD_RADIUS * LAUNCH_PAD_RADIUS) continue;
        if (Math.abs(ent.y - ty - 0.05) > LAUNCH_PAD_CONTACT_Y) continue;

        const cdKey = `lp:${pi}:${ent.key}`;
        const readyAt = this.launchPadCooldowns.get(cdKey) ?? 0;
        if (now < readyAt) continue;

        this.launchPadCooldowns.set(cdKey, now + LAUNCH_PAD_COOLDOWN);
        hits.push({
          key: ent.key,
          boostY: LAUNCH_PAD_BOOST_Y,
          forwardX: forwardX * LAUNCH_PAD_BOOST_FORWARD,
          forwardZ: forwardZ * LAUNCH_PAD_BOOST_FORWARD,
        });
      }
    }
    return hits;
  }

  tryRemoveFromLook(camera: THREE.Camera, maxDist = EDIT_RAY_MAX): number {
    const build = this.findBuildFromRay(camera, maxDist);
    if (!build) return 0;
    const idx = this.placed.findIndex((p) => p.mesh === build.mesh);
    if (idx === -1) return 0;
    if (this.editHighlightTarget === build) this.editHighlightTarget = null;
    return this.removeBuildAtIndex(idx, true);
  }

  /** Reinforce a targeted player build (+50 HP / max HP, cap 200). Returns mat cost on success. */
  tryReinforceFromLook(camera: THREE.Camera, maxDist = 4.2): number {
    const build = this.findBuildFromRay(camera, maxDist);
    if (!build || build.maxHp >= REINFORCE_MAX_HP_CAP) return 0;

    const now = performance.now();
    build.maxHp = Math.min(REINFORCE_MAX_HP_CAP, build.maxHp + REINFORCE_HP_BOOST);
    build.hp = Math.min(build.maxHp, build.hp + REINFORCE_HP_BOOST);
    build.mesh.userData.buildMaxHp = build.maxHp;
    build.mesh.userData.buildHp = build.hp;
    if (!build.mesh.userData.isReinforced) {
      build.mesh.userData.isReinforced = true;
      const oldMat = build.mesh.material as THREE.MeshToonMaterial;
      const metalMat = this.metalReinforcedMat();
      build.mesh.material = metalMat;
      oldMat.dispose();
      build.mesh.userData.baseEmissive = metalMat.emissive.getHex();
      build.mesh.userData.baseEmissiveIntensity = metalMat.emissiveIntensity;
    }
    build.mesh.userData.reinforceFlashUntil = now + REINFORCE_FLASH_MS;
    this.refreshBuildVisual(build, build === this.editHighlightTarget, now);
    return REINFORCE_MAT_COST;
  }

  /** Destroy player builds within a spherical radius. */
  destroyInRadius(cx: number, cy: number, cz: number, radius: number): number {
    const r2 = radius * radius;
    let removed = 0;
    for (let i = this.placed.length - 1; i >= 0; i--) {
      const p = this.placed[i]!;
      const dx = p.mesh.position.x - cx;
      const dy = p.mesh.position.y - cy;
      const dz = p.mesh.position.z - cz;
      if (dx * dx + dy * dy + dz * dz > r2) continue;
      this.removeBuildAtIndex(i, false);
      removed += 1;
    }
    return removed;
  }

  getPlaced(): PlacedBuild[] {
    return this.placed.slice();
  }

  dispose(): void {
    for (const [mesh] of this.buildHpBars) {
      this.cleanupHpBar(mesh);
    }
    if (this.targetHighlightMesh) {
      this.targetHighlightMesh.removeFromParent();
      this.targetHighlightMesh.geometry.dispose();
    }
    this.targetHighlightMat.dispose();
    for (const strip of this.invalidPreviewStrips) {
      strip.geometry.dispose();
      if (strip.material instanceof THREE.Material) strip.material.dispose();
    }
    this.toonGrad.dispose();
    this.ghostGeo.dispose();
    this.ghostMat.dispose();
    this.ghost.removeFromParent();
  }
}
